import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true
});

// Auth endpoints
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const logout = () => api.post('/auth/logout');
export const validateClassCode = (data) => api.post('/auth/validate-class-code', data);
export const registerStudent = (data) => api.post('/auth/join', data);
export const getProfile = () => api.get('/users/profile');
export const updateProfile = (data) => api.put('/users/profile', data);

// Lesson endpoints
export const createLesson = (data) => api.post('/lessons', data);
export const getLessons = () => api.get('/lessons');
export const getLesson = (id) => api.get(`/lessons/${id}`);
export const getLessonQuiz = (id) => api.get(`/lessons/${id}/quiz`);
export const updateLesson = (id, data) => api.put(`/lessons/${id}`, data);
export const deleteLesson = (id) => api.delete(`/lessons/${id}`);
export const generateQuestions = (id, data) => api.post(`/lessons/${id}/generate-questions`, data);

// Add interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're not on a quiz or results page
      const isQuizRoute = window.location.pathname.includes('/quiz/') || 
                         window.location.pathname.includes('/results/');
      if (!window.location.pathname.includes('/login') && !isQuizRoute) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Add these new endpoints
export const submitQuizResults = (lessonId, results) => {
  return api.post(`/quiz-results/${lessonId}`, results);
};

export const submitQuizResultsAuth = (lessonId, results) => {
  return api.post(`/quiz-results/${lessonId}/auth`, results);
};

export const getQuizResults = (resultId) => {
  return api.get(`/quiz-results/${resultId}`);
};

export const getQuizStats = () => {
  return api.get('/quiz-results/admin/stats');
};

export const getLessonResults = (lessonId) => {
  return api.get(`/quiz-results/admin/lesson/${lessonId}`);
};

export const getLessonResultsAnonymous = (lessonId) => {
  return api.get(`/quiz-results/lesson/${lessonId}/anonymous`);
};

export const getLessonResultsByClass = (lessonId, classId) => {
  return api.get(`/quiz-results/lesson/${lessonId}/class/${classId}`);
};

export const getLessonResultsByStudent = (lessonId, studentId) => {
  return api.get(`/quiz-results/lesson/${lessonId}/student/${studentId}`);
};

// Programming lesson results
export const getProgrammingLessonResultsAnonymous = (lessonId) => {
  return api.get(`/programming-submissions/lesson/${lessonId}/anonymous`);
};

export const getProgrammingLessonResultsByClass = (lessonId, classId) => {
  return api.get(`/programming-submissions/lesson/${lessonId}/class/${classId}`);
};

export const getProgrammingLessonResultsByStudent = (lessonId, studentId) => {
  return api.get(`/programming-submissions/lesson/${lessonId}/student/${studentId}`);
};

// School endpoints
export const getSchools = (search) => {
  const params = search ? { params: { search } } : {};
  return api.get('/schools', params);
};

export const createSchool = (data) => {
  return api.post('/schools', data);
};

export const updateSchool = (id, data) => {
  return api.put(`/schools/${id}`, data);
};

export const deleteSchool = (id) => {
  return api.delete(`/schools/${id}`);
};

// Programming Lesson endpoints
export const createProgrammingLesson = (data) => api.post('/programming-lessons', data);
export const getProgrammingLessons = () => api.get('/programming-lessons');
export const getProgrammingLesson = (id) => api.get(`/programming-lessons/${id}`);
export const updateProgrammingLesson = (id, data) => api.put(`/programming-lessons/${id}`, data);
export const deleteProgrammingLesson = (id) => api.delete(`/programming-lessons/${id}`);
export const generateProgrammingTasks = (id, data) => api.post(`/programming-lessons/${id}/generate-tasks`, data);
export const generateSimilarTasks = (originalLessonId, difficulties, language, level, taskCount) => {
  return api.post('/programming-lessons/generate-similar', {
    originalLessonId,
    difficulties,
    language,
    level,
    taskCount
  });
};

// Programming Submission endpoints
export const submitProgrammingCode = (lessonId, taskNumber, code) => {
  return api.post('/programming-submissions', {
    programmingLessonId: lessonId,
    taskNumber,
    studentCode: code
  });
};

export const getProgrammingSubmissions = (lessonId) => {
  return api.get(`/programming-submissions/${lessonId}`);
};

export const requestHint = (submissionId) => {
  return api.post(`/programming-submissions/${submissionId}/hint`);
};

export const requestAdditionalResources = (lessonId, taskNumber, question) => {
  return api.post(`/programming-lessons/${lessonId}/tasks/${taskNumber}/additional-resources`, {
    question
  });
};

// Class management (teacher + student join)
export const getClasses = () => api.get('/classes');
export const createClass = (data) => api.post('/classes', data);
export const rotateClassCode = (classId) => api.post(`/classes/${classId}/rotate-code`);
export const getClassStudents = (classId) => api.get(`/classes/${classId}/students`);
export const kickStudentFromClass = (classId, studentId) => api.delete(`/classes/${classId}/students/${studentId}`);
export const joinClass = (code) => api.post('/classes/join', { code });
export const getMyClasses = () => api.get('/classes/mine/list');
export const getClassCurriculum = (classId) => api.get(`/classes/${classId}/curriculum`);
export const setClassSubjects = (classId, subjectIds) => api.put(`/classes/${classId}/subjects`, { subjectIds });
export const setClassCompetenceAims = (classId, competenceAimIds) => api.put(`/classes/${classId}/competence-aims`, { competenceAimIds });

// Subjects + competence aims (teacher/admin)
export const getSubjects = () => api.get('/subjects');
export const createSubject = (data) => api.post('/subjects', data);
export const getCompetenceAims = (subjectId) => {
  const params = subjectId ? { params: { subjectId } } : {};
  return api.get('/competence-aims', params);
};
export const createCompetenceAim = (data) => api.post('/competence-aims', data);
export const getCompetenceAimSkills = (aimId) => api.get(`/competence-aims/${aimId}/skills`);
export const generateCompetenceAimSkills = (aimId, data) => api.post(`/competence-aims/${aimId}/generate-skills`, data);
export const saveCompetenceAimSkills = (aimId, items) => api.put(`/competence-aims/${aimId}/skills`, { items });
export const generateCompetenceAimKeyPoints = (aimId, skillId, data) =>
  api.post(`/competence-aims/${aimId}/skills/${skillId}/generate-key-points`, data);

// Assignments
export const createAssignment = (data) => api.post('/assignments', data);
export const getAssignments = () => api.get('/assignments');
export const getMyAssignments = () => api.get('/assignments/mine');
export const getResourceAssignments = (resourceType, resourceId) => 
  api.get(`/assignments/resource/${resourceType}/${resourceId}`);
export const updateAssignmentVisibility = (assignmentId, visibleFrom) => 
  api.put(`/assignments/${assignmentId}/visibility`, { visibleFrom });
export const getTeacherStudents = () => api.get('/classes/teacher/students');

// Teacher progress
export const getClassProgress = (classId) => api.get(`/progress/classes/${classId}`);
// Student progress
export const getMyProgress = () => api.get('/progress/mine');
// Individual student progress (teacher)
export const getStudentProgress = (studentId) => api.get(`/progress/students/${studentId}`);

// Admin endpoints
export const getPendingTeachers = () => api.get('/users/admin/pending-teachers');
export const approveTeacher = (teacherId) => api.post(`/users/admin/approve-teacher/${teacherId}`);
export const rejectTeacher = (teacherId) => api.delete(`/users/admin/reject-teacher/${teacherId}`);

// Interactive Lessons
export const createInteractiveLesson = (data) => api.post('/interactive-lessons', data);
export const getInteractiveLessons = () => api.get('/interactive-lessons');
export const getInteractiveLesson = (id) => api.get(`/interactive-lessons/${id}`);
export const updateInteractiveLesson = (id, data) => api.put(`/interactive-lessons/${id}`, data);
export const deleteInteractiveLesson = (id) => api.delete(`/interactive-lessons/${id}`);
export const validateInteractiveAnswer = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/validate-answer`, data);
export const askAITutor = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/ai-tutor`, data);
export const requestInteractiveHint = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/hint`, data);
export const generateCodingTask = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/generate-coding-task`, data);
export const generateMCQ = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/generate-mcq`, data);
export const validateGeneratedTask = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/validate-generated-task`, data);
export const getGeneratedTasks = (lessonId, screenId) => api.get(`/interactive-lessons/${lessonId}/generated-tasks/${screenId}`);
export const saveInteractiveLessonProgress = (lessonId, data) => api.post(`/interactive-lessons/${lessonId}/progress`, data);

export default api; 